<script lang="ts">
	import { type Component } from "svelte"
	import { type ProjectorCommandSender } from "../types"
	import ProjectorRemoteButton from "./ProjectorRemoteButton.svelte"
	import { ArrowUp, ArrowDown, ArrowRight, ArrowLeft, HamburgerIcon } from "luxedo-icons"

	import { type ProjectorCommand, SUPPORTED_COMMANDS } from "../../../projector-commands/src"

	interface Props {
		sendProjectorCommand: ProjectorCommandSender
		hiddenCommands?: Array<ProjectorCommand>
	}

	let { sendProjectorCommand, hiddenCommands = [] }: Props = $props()

	/**
	 * Returns a component or a display string for the specified command
	 */
	function getCommandIndicator(cmd: ProjectorCommand) {
		const commandIndicators: { [key in ProjectorCommand]: Component | string } = {
			down: ArrowDown,
			up: ArrowUp,
			left: ArrowLeft,
			right: ArrowRight,
			enter: "OK",
			escape: "ESC",
			set_input_hdmi1: "HDMI1",
			set_input_hdmi2: "HDMI2",
			menu: HamburgerIcon,
			set_power_off: "OFF",
			set_power_on: "ON",
		}

		return commandIndicators[cmd]
	}

	/**
	 * Returns true if the specified command should be currently hidden
	 * @param cmd the command to hide if relevant
	 */
	function isCmdHidden(cmd: ProjectorCommand) {
		return hiddenCommands.includes(cmd)
	}
</script>

<div id="projector-remote">
	{#each SUPPORTED_COMMANDS as cmd}
		{#if !isCmdHidden(cmd)}
			<ProjectorRemoteButton
				command={cmd}
				sendCommand={sendProjectorCommand}
				title={cmd}
				id={`${cmd}-button`}
			>
				{@const Child = getCommandIndicator(cmd)}
				{#if typeof Child === "string"}
					{Child}
				{:else}
					<Child />
				{/if}
			</ProjectorRemoteButton>
		{/if}
	{/each}
</div>

<style>
	#projector-remote {
		display: grid;
		position: relative;
		padding: 1rem;
		grid-template-columns: 1fr 1fr 1fr;
		align-items: center;
		justify-items: center;
		width: 12rem;
		background-color: var(--color-bg-dark);
		border-radius: var(--br);
	}

	#projector-remote :global(#up-button) {
		grid-row-start: 2;
		grid-row-end: 3;
		grid-column-start: 2;
		grid-column-end: 2;
	}

	#projector-remote :global(#menu-button) {
		grid-row-start: 2;
		grid-row-end: 2;
		grid-column-start: 1;
		grid-column-end: 1;
	}

	#projector-remote :global(#menu-button svg) {
		max-width: 1.5rem;
	}

	#projector-remote :global(#escape-button) {
		grid-row-start: 1;
		grid-row-end: 1;
		grid-column-start: 1;
		grid-column-end: 1;
	}

	#projector-remote :global(#down-button) {
		grid-row-start: 4;
		grid-row-end: 5;
		grid-column-start: 2;
		grid-column-end: 2;
	}

	#projector-remote :global(#left-button) {
		grid-row-start: 3;
		grid-row-end: 4;
		grid-column-start: 1;
		grid-column-end: 1;
	}

	#projector-remote :global(#right-button) {
		grid-row-start: 3;
		grid-row-end: 4;
		grid-column-start: 3;
		grid-column-end: 3;
	}

	#projector-remote :global(#set_power_on-button) {
		grid-row-start: 1;
		grid-row-end: 1;
		grid-column-start: 3;
		grid-column-end: 3;
	}

	#projector-remote :global(#set_power_off-button) {
		grid-row-start: 1;
		grid-row-end: 1;
		grid-column-start: 2;
		grid-column-end: 2;
	}

	#projector-remote :global(#enter-button) {
		grid-row-start: 3;
		grid-row-end: 3;
		grid-column-start: 2;
		grid-column-end: 2;
	}

	#projector-remote :global(#set_input_hdmi1-button) {
		grid-row-start: 5;
		grid-row-end: 5;
		grid-column-start: 1;
		grid-column-end: 1;
	}
	#projector-remote :global(#set_input_hdmi2-button) {
		grid-row-start: 5;
		grid-row-end: 5;
		grid-column-start: 3;
		grid-column-end: 3;
	}
</style>
